import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import qs from 'qs'
import { ElMessage } from 'element-plus'
import * as util from '@/utils/utils'
import { useAppType } from '@/hooks/useAppType.js'
import { useCurrentUser } from '@/hooks/user'

const userHook = useCurrentUser()

const { currentConfigEnvObj } = useAppType()

const appInfo = util.getAppInfo()

const CancelToken = axios.CancelToken

let source = CancelToken.source()

// 统一配置请求头X-JiuZhou-Service
// const prefixHeaders = import.meta.env.VITE_API_PREFIX || "";
const prefixHeaders = currentConfigEnvObj.value.serviceHeaderPrefix || ''
// const env = import.meta.env.VITE_NODE_ENV

const config = {
  method: 'post',
  timeout: 30 * 1000,
  headers: {
    'Content-Type': 'application/json',
    'X-Shanhai-Service': appInfo.key
    // "X-App-Id": appInfo.appId // appId 灰度发布需要-不传默认为1
  },
  maxBodyLength: 10000,
  maxContentLength: 10000,
  paramsSerializer: {
    serialize: function (params) {
      return qs.stringify(params, { arrayFormat: 'repeat' })
    }
  }
}

// 创建axios实例
const service: AxiosInstance = axios.create(config)

const parse = function (str: string) {
  const data = {}
  const p = str.split('&')
  p.forEach((item: string) => {
    const session = item.split('=')
    // @ts-ignore
    data[session[0]] = decodeURIComponent(session[1])
  })
  return data
}

function handleTokenTimeout() {
  ElMessage.error('登录失效，请重新登录')
  // 清除缓存
  util.clearStorage()
  // 跳转前往登录页面
  userHook.reset()
  location.href = `${location.origin}`
}

function showErrorMessage(message?) {
  const errorMsg = message || '服务器繁忙，请稍后再试'
  ElMessage.error(errorMsg)
  return Promise.reject(errorMsg)
}

// request拦截器
service.interceptors.request.use(
  // @ts-ignore
  (config: AxiosRequestConfig) => {
    config.cancelToken = source.token // 写入取消请求的标识

    const { headers, data, url, method, permiss } = config as any
    const service = headers['X-Shanhai-Service']
    const unAllowedSerHeaders = ['AuthSSO', 'DLDJspeciesweb']
    if (!unAllowedSerHeaders.includes(service)) {
      if (prefixHeaders && !headers['X-Shanhai-Service'].includes(prefixHeaders)) {
        headers['X-Shanhai-Service'] = prefixHeaders + headers['X-Shanhai-Service']
      }
    }

    // 如果url中包含/shanhai-admin 路径不需要设置x-shanhai-service
    if (url.includes('/shanhai-admin')) {
      headers['X-Shanhai-Service'] = null
    }

    if (permiss && !headers['x-shanhai-path']) {
      headers['x-shanhai-path'] = permiss
    }

    // 上海通用接口前缀 headers包含SH-GEN的
    if (headers['SH-GEN']) {
      headers['X-Shanhai-Service'] = import.meta.env.VITE_API_NEW_PREFIX + headers['X-Shanhai-Service']
    }

    // const authToken = util.getAuthToken();
    const contentType = headers['Content-Type']
    if (!url.includes('http')) {
      config.url = util.getRequestBaseUrl() + url
    }
    // 测试环境用---线上注释
    // if (!url.includes("http")) {
    //   if (url.startsWith('/test') && env === 'test') {
    //     config.url = url
    //   } else {
    //     config.url = util.getRequestBaseUrl() + url
    //   }
    // }
    // 测试环境用---线上注释
    // if (authToken) {
    //   config.headers["X-JiuZhou-Authorization"] = authToken;
    // } else {
    //   util.goToAuthLoginPage();
    // }
    if (userHook.info.token) {
      headers['Authorization'] = userHook.info.token
    }

    switch (method) {
      case 'get':
        // get参数编码
        if (config.data) {
          let url = config.url as string
          url += '?'
          const keys = Object.keys(config.data)
          for (const key of keys) {
            if (config.data[key] !== void 0 && config.data[key] !== null) {
              url += `${key}=${encodeURIComponent(config.data[key])}&`
            }
          }
          url = url.substring(0, url.length - 1)
          config.data = {}
          config.url = url
        }
        break
      case 'delete':
      case 'head':
      case 'options':
        break
      case 'post':
      case 'put':
      case 'patch':
        if (typeof config.data === 'string') {
          const str = config.data
          try {
            config.data = JSON.parse(str)
          } catch (e) {
            // data数据在服务那边处理后变成了 name=val&...形式
            config.data = parse(str)
          }
        }
        if (contentType === 'application/x-www-form-urlencoded') {
          config.data = qs.stringify(config.data)
        } else if (contentType === 'multipart/form-data') {
          const formData = new FormData()
          for (const key in data) {
            formData.append(key, data[key])
            config.data = formData
          }
        }
        break
      default:
        break
    }
    return config
  },
  (error: AxiosError) => {
    // Do something with request error
    console.log(error) // for debug
    Promise.reject(error)
  }
)

export const responseInterceptorFunc = response => {
  const {
    data: { code, data, message, msg, errcode },
    config,
    status,
    errmsg
  } = response
  if (config.silent) {
    return response.data
  }
  let result = null
  // token失效,用户被删除
  if ([1040401, 2000001].includes(code)) {
    ElMessage({
      message: message || msg || errmsg,
      type: 'error'
    })
    userHook.reset()
    location.href = `${location.origin}`
    return Promise.reject(result)
  }
  // 操作频繁
  if (code === 2000009) {
    ElMessage({
      message: message || msg || errmsg,
      type: 'error'
    })
    return Promise.reject(result)
  }
  if (config.responseType === 'blob') {
    // 下载文件
    result = response
  } else {
    if (status === 200) {
      if (code === 'ok' || code === 200 || errcode === 0) {
        result = data === undefined || data === null || data === '' ? true : data
      } else if (code === 'Unauthorized') {
        showErrorMessage()
        handleTokenTimeout()
      } else if (code === 'uploadError') {
        const errorMsg = message || msg
        ElMessage.error(errorMsg)
        return Promise.reject(data)
      } else {
        return showErrorMessage(message || msg || errmsg)
      }
    } else if (status === 401) {
      handleTokenTimeout()
      source.cancel()
      source = CancelToken.source()
      return showErrorMessage()
    } else {
      return showErrorMessage(message || msg || errmsg)
    }
  }
  result = data === undefined || data === null || data === '' ? true : data
  return result
}

// service.interceptors.request.use(requestInterceptorFunc, (error) => Promise.reject(error))

service.interceptors.response.use(responseInterceptorFunc, error => {
  if (error.response) {
    const {
      response: {
        status,
        data: { message, msg, errmsg, code }
      }
    } = error
    // token失效,用户被删除
    if ([1040401, 2000001].includes(code)) {
      ElMessage({
        message: message || msg || errmsg,
        type: 'error'
      })
      userHook.reset()
      location.href = `${location.origin}`
      source.cancel()
      source = CancelToken.source()
      return Promise.reject(errmsg)
    }
    if (status === 401) {
      source.cancel()
      source = CancelToken.source()
      handleTokenTimeout()
    } else {
      return showErrorMessage(message || msg || errmsg || error?.message)
    }
  }
  return showErrorMessage()
})

export default service
